import { Tenant, User } from '../types';
import { formatDate, getTenantLevelLabel, getTenantTypeLabel, getUserFullName } from './format';

/**
 * Convert an array of objects to CSV format
 * @param data Array of objects
 * @param headers Object mapping field names to display headers
 * @returns CSV string
 */
export const convertToCSV = <T extends Record<string, any>>(
  data: T[],
  headers: Record<string, string>
): string => {
  if (data.length === 0) return '';

  // Get all header keys
  const headerKeys = Object.keys(headers);
  
  // Create header row
  const headerRow = headerKeys.map(key => `"${headers[key]}"`).join(',');
  
  // Create data rows
  const rows = data.map(item => {
    return headerKeys.map(key => {
      // Handle null, undefined and convert to string with quotes
      const value = item[key] !== null && item[key] !== undefined ? item[key] : '';
      return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : `"${value}"`;
    }).join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...rows].join('\n');
};

/**
 * Download data as a CSV file
 * @param data CSV string
 * @param filename Filename without extension
 */
export const downloadCSV = (data: string, filename: string): void => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export tenants data to CSV
 * @param tenants Array of tenant objects
 */
export const exportTenantsToCSV = (tenants: Tenant[]): void => {
  // Define CSV headers
  const headers: Record<string, string> = {
    name: 'Name',
    tenant_code: 'Tenant Code',
    tenant_type: 'Type',
    level: 'Level',
    group: 'Group',
    status: 'Status',
    parent_tenant_id: 'Parent Tenant ID',
    start_date: 'Start Date',
    end_date: 'End Date',
    onboarding_date: 'Onboarding Date',
    contract_renewal_status: 'Contract Renewal Status',
    threshold_per_day: 'Threshold Per Day',
    online_retention: 'Online Retention',
    offline_retention: 'Offline Retention',
    email_to: 'Email To',
    email_cc: 'Email CC',
    created_at: 'Created At'
  };
  
  // Format tenant data for CSV
  const formattedTenants = tenants.map(tenant => ({
    ...tenant,
    level: getTenantLevelLabel(tenant.level),
    tenant_type: getTenantTypeLabel(tenant.tenant_type),
    start_date: formatDate(tenant.start_date),
    end_date: formatDate(tenant.end_date),
    onboarding_date: formatDate(tenant.onboarding_date),
    created_at: formatDate(tenant.created_at)
  }));
  
  // Convert to CSV and download
  const csv = convertToCSV(formattedTenants, headers);
  downloadCSV(csv, `tenants_export_${new Date().toISOString().split('T')[0]}`);
};

/**
 * Export users data to CSV
 * @param users Array of user objects
 */
export const exportUsersToCSV = (users: User[]): void => {
  // Define CSV headers
  const headers: Record<string, string> = {
    username: 'Username',
    fullName: 'Full Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    last_login: 'Last Login',
    created_at: 'Created At'
  };
  
  // Format user data for CSV
  const formattedUsers = users.map(user => ({
    ...user,
    fullName: getUserFullName(user),
    role: user.role.charAt(0).toUpperCase() + user.role.slice(1), // Capitalize
    last_login: user.last_login ? formatDate(user.last_login) : 'Never',
    created_at: formatDate(user.created_at)
  }));
  
  // Convert to CSV and download
  const csv = convertToCSV(formattedUsers, headers);
  downloadCSV(csv, `users_export_${new Date().toISOString().split('T')[0]}`);
}; 